import { forwardRef, useEffect, useRef, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export const controlBase =
  'w-full rounded-md border border-line-strong bg-surface text-ink placeholder:text-subtle ' +
  'transition-colors hover:border-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-muted [&[readonly]]:bg-surface-2 ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/20 ' +
  // Phones: 44px touch target and 16px text (prevents iOS focus zoom).
  'max-sm:min-h-11 max-sm:text-base';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, leading, trailing, ...rest }, ref) {
  if (!leading && !trailing) return <input ref={ref} className={cn(controlBase, 'h-9 px-3 text-sm', className)} {...rest} />;
  return (
    <div className="relative flex items-center">
      {leading && <span className="pointer-events-none absolute left-2.5 flex text-subtle">{leading}</span>}
      <input
        ref={ref}
        className={cn(controlBase, 'h-9 text-sm', leading ? 'pl-8' : 'pl-3', trailing ? 'pr-9' : 'pr-3', className)}
        {...rest}
      />
      {trailing && <span className="absolute right-1.5 flex items-center">{trailing}</span>}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Character counter under the box. Default: shown when `maxLength` ≥ 200. */
  showCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 3, showCount, maxLength, onChange, ...rest },
  ref,
) {
  const inner = useRef<HTMLTextAreaElement | null>(null);
  const [length, setLength] = useState(0);
  const counted = (showCount ?? (maxLength !== undefined && maxLength >= 200)) && maxLength !== undefined;
  // Uncontrolled (react-hook-form register / reset) values change without events — re-read after each render.
  // setLength bails out when unchanged, so this cannot loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (counted && inner.current) setLength(inner.current.value.length);
  });
  const area = (
    <textarea
      ref={(el) => {
        inner.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      rows={rows}
      maxLength={maxLength}
      onChange={(e) => {
        if (counted) setLength(e.target.value.length);
        onChange?.(e);
      }}
      className={cn(controlBase, 'px-3 py-2 text-sm leading-relaxed', className)}
      {...rest}
    />
  );
  if (!counted) return area;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {area}
      <span
        aria-hidden
        className={cn('self-end text-[11px] tabular leading-none', length >= maxLength ? 'text-danger' : 'text-subtle', length === 0 && 'invisible')}
      >
        {length.toLocaleString('vi-VN')}/{maxLength.toLocaleString('vi-VN')}
      </span>
    </div>
  );
});

export { Select, type SelectOption, type SelectProps } from './SelectMenu';

/** Calendar date (DateOnly "yyyy-MM-dd"). */
export const DateInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function DateInput(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} type="date" className={cn(controlBase, 'h-9 px-3 text-sm tabular', className)} {...rest} />;
});

/** Date-time in Vietnam local time (to the second). Convert with lib/dates localInputToUtc / utcToLocalInput. */
export const DateTimeInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function DateTimeInput(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} type="datetime-local" step={1} className={cn(controlBase, 'h-9 px-3 text-sm tabular', className)} {...rest} />;
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox({ label, description, className, id, ...rest }, ref) {
  return (
    <label htmlFor={id} className={cn('inline-flex cursor-pointer items-start gap-2.5 py-0.5 text-sm leading-5', rest.disabled && 'cursor-not-allowed opacity-60', className)}>
      <input ref={ref} id={id} type="checkbox" className="mt-0.5 size-4 shrink-0 cursor-pointer rounded border-line-strong accent-accent" {...rest} />
      <span className="min-w-0">
        <span className="text-ink">{label}</span>
        {description && <span className="mt-0.5 block text-xs leading-normal text-subtle">{description}</span>}
      </span>
    </label>
  );
});

export const Radio = forwardRef<HTMLInputElement, CheckboxProps>(function Radio({ label, description, className, id, ...rest }, ref) {
  return (
    <label htmlFor={id} className={cn('inline-flex cursor-pointer items-start gap-2.5 py-0.5 text-sm leading-5', rest.disabled && 'cursor-not-allowed opacity-60', className)}>
      <input ref={ref} id={id} type="radio" className="mt-0.5 size-4 shrink-0 cursor-pointer accent-accent" {...rest} />
      <span className="min-w-0">
        <span className="text-ink">{label}</span>
        {description && <span className="mt-0.5 block text-xs leading-normal text-subtle">{description}</span>}
      </span>
    </label>
  );
});
